import React, { useEffect, useState, useContext } from 'react';
import { saveLoginDataWithLogin, resetNavigationToHome, callConnectionConfig, fetchAndGetActiveKnowledgeId, fetchProjectConfig } from '../../../actions/app.action';
import { SET_USER_INFO_KEY_VALUE, LOGOUT_USER, ACTIVATE_LOADER, DEACTIVATE_LOADER } from '../../../actions/app.type';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../utils/AuthContent';

interface RootState {
  loginResource: any;
}

interface ProjectProfile {
  project_id: number;
  profile_id: number;
}


export const useProfileSwitch = (onClose?: () => void) => {

  const dispatch = useDispatch();
  const { loginData, activeKnowledgeId } = useSelector((state: RootState) => ({
    loginData: state.loginResource,
    activeKnowledgeId: state.loginResource.activeKnowledgeId,
  }));
  const navigate = useNavigate();
  const { keycloak } = useContext(AuthContext);
  const [profileShowData, setprofileShowData] = React.useState<{ id: number; name: string }[]>([]);
  const [showProjectList, setshowProjectList] = useState(false);
  const [projectData, setprojectData] = React.useState<{ project_id: number }[]>([]);
  const [isApiCalling, setIsApiCalling] = useState(false);
const [pendingProjectId, setPendingProjectId] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const tempProfile = [...(loginData.userProfile || [])];
    const profiles: { id: number; name: string }[] = [];

    tempProfile
      .filter((item) => item.companyId && item.projectProfile?.length)
      .forEach((item) => {
        item.projectProfile.forEach((prof: any) => {
          if (prof.project_id === loginData.preferredProject) {
            const alreadyExists = profiles.some((p) => p.id === prof.profile_id);
            if (!alreadyExists) {
              const matchedProfile = loginData.profileData.find(
                (p: any) => p.id === prof.profile_id
              );
              if (matchedProfile) {
                profiles.push({
                  id: matchedProfile.id,
                  name: matchedProfile.name,
                });
              }
            }
          }
        });
      });

    if (isMounted) {
      setprofileShowData(profiles);

      const selectedCompany = loginData.company_data?.find(
        (item: any) => item.companyId === loginData.preferredCompany
      );

      if (selectedCompany) {
        setprojectData(selectedCompany.projectList || []);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [loginData]);

  /**
   * Helper to set full-screen loader
   */
  const setLoader = (active: boolean) => {
    dispatch({
      type: active ? ACTIVATE_LOADER : DEACTIVATE_LOADER,
      payload: active,
    });
  };

  /**
   * 🧩 Utility: Get merged project profiles for a company
   */
  const getMergedCompanyProfiles = (companyId: number): ProjectProfile[] => {
    const companyProfiles = loginData.userProfile
      .filter((item: any) => item.companyId === companyId)
      .flatMap((item: any) => item.projectProfile || []);

    // Deduplicate project_id + profile_id pairs
    const uniqueProfiles = Array.from(
      new Map(
        companyProfiles.map((p: any) => [`${p.project_id}-${p.profile_id}`, p])
      ).values()
    ) as ProjectProfile[];

    return uniqueProfiles;
  };

  /**
   * ✅ Fixed: findAppropriateProfile
   */
  const findAppropriateProfile = (
    companyId: number,
    projectId: number,
    currentProfile: string | number
  ): string => {
    const companyProfiles: ProjectProfile[] = getMergedCompanyProfiles(companyId);
    let newPreferredProfile = String(currentProfile);

    const availableProfilesForProject: ProjectProfile[] = companyProfiles.filter(
      (prof: ProjectProfile) => prof.project_id === projectId
    );

   

    if (availableProfilesForProject.length > 0) {
      const currentProfileAvailable: ProjectProfile | undefined = availableProfilesForProject.find(
        (prof: ProjectProfile) => prof.profile_id == currentProfile
      );

      if (currentProfileAvailable) {
        newPreferredProfile = String(currentProfile);
      } else {
        newPreferredProfile = String(availableProfilesForProject[0].profile_id);
      }
    } else {
      const currentProfileValid: boolean = companyProfiles.some(
        (prof: ProjectProfile) => prof.profile_id == currentProfile
      );

      if (!currentProfileValid && companyProfiles.length > 0) {
        newPreferredProfile = String(companyProfiles[0].profile_id);
      }
    }

    return newPreferredProfile;
  };

  /**
   * Common performContextReset logic
   */
  const performContextReset = async (
    updatedPreferences: {
      preferredCompany?: number;
      preferredProject?: number;
      preferredProfile?: string;
    },
    shouldNavigateHome: boolean = false
  ) => {
    dispatch(resetNavigationToHome() as any);

    if (updatedPreferences.preferredCompany !== undefined) {
      dispatch({
        type: SET_USER_INFO_KEY_VALUE,
        payload: { key: 'preferredCompany', value: updatedPreferences.preferredCompany },
      });
    }
    if (updatedPreferences.preferredProject !== undefined) {
      dispatch({
        type: SET_USER_INFO_KEY_VALUE,
        payload: { key: 'preferredProject', value: updatedPreferences.preferredProject },
      });
      ['selectedRole', 'selectedBrands', 'selectedFunctions', 'selectedGoals'].forEach((key) => {
        dispatch({
          type: SET_USER_INFO_KEY_VALUE,
          payload: { key, value: undefined },
        });
      });
    }
    if (updatedPreferences.preferredProfile !== undefined) {
      dispatch({
        type: SET_USER_INFO_KEY_VALUE,
        payload: { key: 'preferredProfile', value: updatedPreferences.preferredProfile },
      });
    }

    const updatedLoginData = {
      ...loginData,
      ...updatedPreferences,
    };

    // Pass regenerate: true when user is switching profile/company/workspace
    await saveLoginDataWithLogin(dispatch, updatedLoginData, true);

    if (shouldNavigateHome) {
      navigate('/', { replace: true });
    }
  };

  /**
   * ✅ Fixed: changeCompany logic
   */
  const handleAutocompleteCompanyChange = async (companyId: number) => {
    if (loginData.preferredCompany === companyId || isApiCalling) return;

    setIsApiCalling(true);
    setLoader(true);

    try {
      const selectedCompany = loginData.company_data.find((item: any) => item.companyId === companyId);
      if (!selectedCompany) return;

      setprojectData(selectedCompany.projectList);

      if (!selectedCompany.projectList?.length) return;

      const companyProfiles: ProjectProfile[] = getMergedCompanyProfiles(companyId);
      if (!companyProfiles?.length) return;

      let newPreferredProject: number;
      let newPreferredProfile: string;

      const currentProfileInNewCompany = companyProfiles.find(
        (prof) => prof.profile_id == loginData.preferredProfile
      );

      if (currentProfileInNewCompany) {
        newPreferredProfile = String(currentProfileInNewCompany.profile_id);
        newPreferredProject = Number(currentProfileInNewCompany.project_id);
        const projectExists = selectedCompany.projectList.find(
          (proj: any) => proj.project_id === newPreferredProject
        );
        if (!projectExists) {
          const another = companyProfiles.find(
            (prof) =>
              prof.profile_id == loginData.preferredProfile &&
              selectedCompany.projectList.some((proj: any) => proj.project_id === prof.project_id)
          );
          newPreferredProject = another
            ? Number(another.project_id)
            : Number(selectedCompany.projectList[0].project_id);
        }
      } else {
        const first = companyProfiles[0];
        newPreferredProfile = String(first.profile_id);
        newPreferredProject = Number(first.project_id);
        const projectExists = selectedCompany.projectList.find(
          (proj: any) => proj.project_id === newPreferredProject
        );
        if (!projectExists) {
          newPreferredProject = Number(selectedCompany.projectList[0].project_id);
          const profileForProject = companyProfiles.find(
            (prof) => prof.project_id === newPreferredProject
          );
          if (profileForProject) newPreferredProfile = String(profileForProject.profile_id);
        }
      }

      await performContextReset(
        { preferredCompany: companyId, preferredProject: newPreferredProject, preferredProfile: newPreferredProfile },
        true
      );

      const newActiveKnowledgeId = await fetchAndGetActiveKnowledgeId(newPreferredProject);
      await fetchProjectConfig(dispatch, newPreferredProject);
      const updatedLoginData = { ...loginData, preferredCompany: companyId, preferredProject: newPreferredProject, preferredProfile: newPreferredProfile };
      await callConnectionConfig(updatedLoginData, newActiveKnowledgeId);

      onClose?.();
    } catch (error) {
      console.error('Error changing company:', error);
    } finally {
      setLoader(false);
      setIsApiCalling(false);
    }
  };

  const changeCompany = async (event: React.ChangeEvent<HTMLSelectElement>, val: number) => {
    if (!event || loginData.preferredCompany === val || isApiCalling) return;

    setIsApiCalling(true);
    // Show full-screen loader
    setLoader(true); 

    try {
      const selectedCompany = loginData.company_data.find((item: any) => item.companyId === val);
      if (!selectedCompany) {
        console.error('Selected company not found');
        return;
      }

      setprojectData(selectedCompany.projectList);

      if (!selectedCompany.projectList || selectedCompany.projectList.length === 0) {
        console.error('No projects available for selected company');
        return;
      }

      const companyProfiles: ProjectProfile[] = getMergedCompanyProfiles(val);

      if (!companyProfiles || companyProfiles.length === 0) {
        console.error('No user profile data found for selected company');
        return;
      }

      let newPreferredProject: number;
      let newPreferredProfile: string;

      const currentProfileInNewCompany: ProjectProfile | undefined = companyProfiles.find(
        (prof: ProjectProfile) => prof.profile_id == loginData.preferredProfile
      );

      if (currentProfileInNewCompany) {
        newPreferredProfile = String(currentProfileInNewCompany.profile_id);
        newPreferredProject = Number(currentProfileInNewCompany.project_id);

        const projectExists = selectedCompany.projectList.find(
          (proj: any) => proj.project_id === newPreferredProject
        );

        if (!projectExists) {
          const anotherProjectWithSameProfile: ProjectProfile | undefined = companyProfiles.find(
            (prof: ProjectProfile) =>
              prof.profile_id == loginData.preferredProfile &&
              selectedCompany.projectList.some((proj: any) => proj.project_id === prof.project_id)
          );

          if (anotherProjectWithSameProfile) {
            newPreferredProject = Number(anotherProjectWithSameProfile.project_id);
          } else {
            newPreferredProject = Number(selectedCompany.projectList[0].project_id);
          }
        }
      } else {
        const firstAvailableProfile: ProjectProfile = companyProfiles[0];
        newPreferredProfile = String(firstAvailableProfile.profile_id);
        newPreferredProject = Number(firstAvailableProfile.project_id);

        const projectExists = selectedCompany.projectList.find(
          (proj: any) => proj.project_id === newPreferredProject
        );

        if (!projectExists) {
          newPreferredProject = Number(selectedCompany.projectList[0].project_id);
          const profileForProject: ProjectProfile | undefined = companyProfiles.find(
            (prof: ProjectProfile) => prof.project_id === newPreferredProject
          );
          if (profileForProject) {
            newPreferredProfile = String(profileForProject.profile_id);
          }
        }
      }


      await performContextReset(
        {
          preferredCompany: val,
          preferredProject: newPreferredProject,
          preferredProfile: newPreferredProfile,
        },
        true
      );

      // Fetch updated domain data to get new activeKnowledgeId
      const newActiveKnowledgeId = await fetchAndGetActiveKnowledgeId(newPreferredProject);

      // Fetch project config for thinking mode
      await fetchProjectConfig(dispatch, newPreferredProject);

      // Call connection config API if user is business analyst with updated knowledge ID
      const updatedLoginData = {
        ...loginData,
        preferredCompany: val,
        preferredProject: newPreferredProject,
        preferredProfile: newPreferredProfile,
      };
      await callConnectionConfig(updatedLoginData, newActiveKnowledgeId);
     // Hide full-screen loader
      setLoader(false);
    } catch (error) {
      console.error('Error changing company:', error);
    } finally {
      // Hide full-screen loader
      setLoader(false);
      setIsApiCalling(false);
    }
  };

  const showProjectName = () => {
    if (!projectData || projectData.length === 0) return;

    const preferredProjectId = loginData?.preferredProject
      ? Number(loginData.preferredProject)
      : null;

    const preferredProject = projectData.find(
      (project) => project && project.project_id === preferredProjectId
    );

    if (preferredProject) {
      const newProjectData = [
        preferredProject,
        ...projectData.filter((project) => project.project_id !== preferredProjectId),
      ];
      setprojectData(newProjectData);
    }

    setshowProjectList(true);
  };


  const hideProjectName = () => {
    setshowProjectList(false);
  };

 const handleRadioButtonChangeProject = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (!e) return;
  setPendingProjectId(Number(e.target.value));
};

const applyProjectChange = async () => {
  const projectIdNum = Number(pendingProjectId);
  if (
    projectIdNum === null ||
    loginData.preferredProject === projectIdNum ||
    isApiCalling
  ) {
    hideProjectName();
    return;
  }

  setIsApiCalling(true);
   // Show loader
  setLoader(true);

  try {
    const newPreferredProfile = findAppropriateProfile(
      loginData.preferredCompany,
      projectIdNum,
      loginData.preferredProfile
    );

    await performContextReset(
      {
        preferredProject: projectIdNum,
        preferredProfile: newPreferredProfile,
      },
      true
    );

    // Fetch updated domain data
    const newActiveKnowledgeId = await fetchAndGetActiveKnowledgeId(projectIdNum);

    // Fetch project config for thinking mode
    await fetchProjectConfig(dispatch, projectIdNum);

    // Update connection config
    const updatedLoginData = {
      ...loginData,
      preferredProject: projectIdNum,
      preferredProfile: newPreferredProfile,
    };
    await callConnectionConfig(updatedLoginData, newActiveKnowledgeId);

    hideProjectName();
  } catch (error) {
    console.error('Error applying project change:', error);
  } finally {
    setLoader(false);
    setIsApiCalling(false);
    setPendingProjectId(null);
  }
};



  const changeProfile = async (
    e: React.ChangeEvent<HTMLSelectElement>,
    val: string,
    _profileShowData: any
  ) => {
    if (!val || loginData.preferredProfile === val || isApiCalling) return;

    setIsApiCalling(true);
    // Show full-screen loader
    setLoader(true); 

    try {
      await performContextReset({
        preferredProfile: val,
      });

      // Fetch updated domain data to get new activeKnowledgeId (project hasn't changed, but we ensure we have latest)
      const newActiveKnowledgeId = await fetchAndGetActiveKnowledgeId(loginData.preferredProject);

      // Call connection config API if user is business analyst with updated knowledge ID
      const updatedLoginData = {
        ...loginData,
        preferredProfile: val,
      };
      await callConnectionConfig(updatedLoginData, newActiveKnowledgeId);
      onClose?.();
    } catch (error) {
      console.error('Error changing profile:', error);
    } finally {
       // Hide full-screen loader
      setLoader(false);
      setIsApiCalling(false);
    }
  };

  const submitLogout = () => {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('authentication');
      localStorage.removeItem('refreshToken');

      dispatch({
        type: LOGOUT_USER,
        payload: {},
      });

      const basePath = window.location.pathname || '/';

      if (keycloak) {
        keycloak.logout({
          redirectUri: window.location.origin + basePath,
        });
      } else {
        window.location.href = basePath;
      }
    } catch (error) {
      localStorage.clear();
      window.location.href = window.location.pathname || '/';
    }
  };

  const handleAutocompleteProjectChange = async (projectId: number) => {
    if (projectId === loginData.preferredProject || isApiCalling) return;

    setIsApiCalling(true);
    setLoader(true);

    try {
      const newPreferredProfile = findAppropriateProfile(
        loginData.preferredCompany,
        projectId,
        loginData.preferredProfile
      );

      await performContextReset(
        { preferredProject: projectId, preferredProfile: newPreferredProfile },
        true
      );

      const newActiveKnowledgeId = await fetchAndGetActiveKnowledgeId(projectId);
      await fetchProjectConfig(dispatch, projectId);

      const updatedLoginData = {
        ...loginData,
        preferredProject: projectId,
        preferredProfile: newPreferredProfile,
      };
      await callConnectionConfig(updatedLoginData, newActiveKnowledgeId);
      onClose?.();
    } catch (error) {
      console.error('Error applying project change:', error);
    } finally {
      setLoader(false);
      setIsApiCalling(false);
    }
  };

  return {
    showProjectList,
    loginData,
    projectData,
    profileShowData,
    submitLogout,
    changeCompany,
    hideProjectName,
    showProjectName,
    changeProfile,
    handleRadioButtonChangeProject,
    applyProjectChange,
    pendingProjectId,
    handleAutocompleteProjectChange,
    handleAutocompleteCompanyChange,
  };
};
